import { normalizeCanvasData } from "./api";
import { type CanvasData } from "./apitypes";

interface PostMessageRequest {
  type: "REQUEST_CANVAS_DATA";
}

interface PostMessageResponse {
  type: "CANVAS_DATA";
  data: CanvasData;
}

export class PostMessageTimeoutError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "PostMessageTimeoutError";
  }
}

let fetchedResponse: CanvasData | null = null;

export async function getCanvasViaPostMessage(timeoutMs: number = 15000): Promise<CanvasData> {
  return new Promise((resolve, reject) => {
    if (fetchedResponse) {
      resolve(normalizeCanvasData(fetchedResponse as CanvasData));
      return;
    }

    const timeoutId = setTimeout(() => {
      cleanup();
      reject(new PostMessageTimeoutError(`Timeout waiting for canvas data after ${timeoutMs}ms`));
    }, timeoutMs);

    const handleMessage = (event: MessageEvent) => {
      if (event.data && event.data.type === "CANVAS_DATA") {
        const response = event.data as PostMessageResponse;
        cleanup();
        const normalized = normalizeCanvasData(response.data);
        fetchedResponse = normalized;
        resolve(normalized);
        return;
      }
    };

    const cleanup = () => {
      clearTimeout(timeoutId);
      fetchedResponse = null;
      window.removeEventListener("message", handleMessage);
    };

    window.addEventListener("message", handleMessage);

    const request: PostMessageRequest = {
      type: "REQUEST_CANVAS_DATA",
    };

    let targetWindow: Window | null = null;

    if (window.opener) {
      targetWindow = window.opener;
      console.log("Preview mode: Using window.opener (new tab mode)");
    } else if (window.parent && window.parent !== window) {
      targetWindow = window.parent;
      console.log("Preview mode: Using window.parent (iframe mode)");
    }

    if (targetWindow) {
      targetWindow.postMessage(request, "*");
    } else {
      cleanup();
      reject(new Error("No parent or opener window found. postMessage mode requires iframe or new tab context."));
    }
  });
}
